import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Shipping from "./components/Shipping/Shipping";
import CustomerData from "./components/CustomerData/CustomerData";
import Billing from "./components/Billing/Billing";
import CheckoutSummary from "./components/CheckoutSummary/CheckoutSummary";
import { trackEvent, trackEcommerceEvent } from "../../hooks/useGTM";
import metaPixelCatalogService from "../../services/metaPixelCatalogService";
import {
  validateShippingData,
  validateCustomerData,
  validateBillingData,
  prepareOrderPayload,
} from "../../utils/orderUtils";
import { finalizeCartCheckout } from "../../lib/finalizeCheckout";
import { findOrCreateCart } from "../../lib/cartApis";
import {
  scrollToNextSection,
  lockBodyScroll,
  unlockBodyScroll,
} from "../../utils/scrollUtils";

import styles from "./Checkout.module.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { BillingRef } from "./components/Billing/Billing";

const Checkout: React.FC = () => {
  const navigate = useNavigate();

  const [cartGroups, setCartGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] =
    useState<boolean>(false);

  const [shippingData, setShippingData] = useState<any>({});
  const [customerData, setCustomerData] = useState<any>({});
  const [billingData, setBillingData] = useState<any>({});
  const [shippingConfirmed, setShippingConfirmed] = useState(false);
  const [customerDataConfirmed, setCustomerDataConfirmed] = useState(false);
  const [billingConfirmed, setBillingConfirmed] = useState(false);

  // Add refs for each step component
  const shippingRef = useRef<HTMLDivElement>(null);
  const customerDataRef = useRef<HTMLDivElement>(null);
  const billingRef = useRef<BillingRef>(null);
  const billingElementRef = useRef<HTMLDivElement>(null);

  // Clean pricing state from cart API
  const [cleanTotals, setCleanTotals] = useState({
    originalSubtotal: 0,
    promotionalDiscountTotal: 0,
    subtotalAfterPromotions: 0,
    couponDiscountTotal: 0,
    subtotalAfterCoupons: 0,
    deliveryFeeTotal: 0,
    grandTotal: 0,
  });

  // Legacy cart totals for backward compatibility with other components
  const [cartTotals, setCartTotals] = useState({
    totalPrice: 0,
    totalDeliveryFee: 0,
    cartTotal: 0,
  });

  // Discount state

  const [appliedCouponCode, setAppliedCouponCode] = useState<string>("");

  // Reservation timer state
  const [reservationTime, setReservationTime] = useState({
    expiryTime: null as Date | null,
    timeLeft: 0,
    isExpiring: false,
  });

  // Add state for loading message
  const [loadingMessage, setLoadingMessage] =
    useState<string>("Procesando pago");

  useEffect(() => {
    const fetchCartData = async () => {
      try {
        setLoading(true);
        const cartData = await findOrCreateCart();

        if (cartData) {
          setCartGroups(cartData.groups || []);

          // Set clean pricing totals from the cleanPricing response
          const cleanResponse = cartData.cleanPricing;
          setCleanTotals({
            originalSubtotal: cleanResponse?.totals?.originalSubtotal || 0,
            promotionalDiscountTotal:
              cleanResponse?.totals?.promotionalDiscountTotal || 0,
            subtotalAfterPromotions:
              cleanResponse?.totals?.subtotalAfterPromotions || 0,
            couponDiscountTotal:
              cleanResponse?.totals?.couponDiscountTotal || 0,
            subtotalAfterCoupons:
              cleanResponse?.totals?.subtotalAfterCoupons || 0,
            deliveryFeeTotal: cleanResponse?.totals?.deliveryFeeTotal || 0,
            grandTotal: cleanResponse?.totals?.grandTotal || 0,
          });

          // Legacy cart totals for backward compatibility
          setCartTotals({
            totalPrice: cartData.totalPrice || 0,
            totalDeliveryFee: cartData.totalDeliveryFee || 0,
            cartTotal: cartData.finalTotal || 0,
          });

          // Set applied coupon code
          setAppliedCouponCode(
            cartData.appliedCoupons && cartData.appliedCoupons.length > 0
              ? cartData.appliedCoupons[0].code
              : ""
          );

          // Log cart totals after fetching
          console.log("[Checkout] Cart data after fetch:", {
            legacyTotals: {
              totalPrice: cartData.totalPrice,
              totalDeliveryFee: cartData.totalDeliveryFee,
              cartTotal: cartData.finalTotal,
            },
            cleanPricing: cleanResponse?.totals,
            appliedCoupons: cartData.appliedCoupons,
          });
        } else {
          throw new Error("No cart data available");
        }
      } catch (err) {
        console.error("Error fetching cart data:", err);
        setError("Error fetching cart data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCartData();

    // Track checkout page view
    trackEvent("checkout_page_view", {
      page_title: "Checkout Page",
      page_section: "checkout",
    });
  }, []);

  useEffect(() => {
    // Log the amount passed to Billing whenever cleanTotals.grandTotal changes
    console.log(
      "[Checkout] Passing grand total to Billing:",
      cleanTotals.grandTotal
    );
  }, [cleanTotals.grandTotal]);

  // Setup reservation timer
  useEffect(() => {
    // Get expiry time from session storage
    const expiryTimeStr = sessionStorage.getItem("checkoutReservationExpiry");
    if (!expiryTimeStr) return;

    const expiryTime = new Date(expiryTimeStr);
    setReservationTime((prev) => ({ ...prev, expiryTime }));

    // Set up timer to update every second
    const timer = setInterval(() => {
      const now = new Date();
      const timeLeft = Math.max(
        0,
        Math.floor((expiryTime.getTime() - now.getTime()) / 1000)
      );

      setReservationTime((prev) => ({
        ...prev,
        timeLeft,
        isExpiring: timeLeft < 180, // Less than 3 minutes
      }));

      if (timeLeft <= 0) {
        clearInterval(timer);
        // Handle expiry - redirect or show message
        alert(
          "Su sesión de compra ha expirado. Por favor, inténtelo de nuevo."
        );
        navigate("/cart");
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  // Block/unblock body scroll when overlay is shown/hidden
  useEffect(() => {
    if (isProcessingPayment) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
    // Clean up on unmount
    return () => {
      unlockBodyScroll();
    };
  }, [isProcessingPayment]);

  const handleOrderSubmit = async (billingDataOverride?: any) => {
    console.log("Checkout: handleOrderSubmit called");
    const finalBillingData = billingDataOverride || billingData;

    // Detailed logging for Payme payment response
    if (finalBillingData.paymentMethod === "payme") {
      console.log("Checkout: Processing Payme payment", {
        response: finalBillingData.paymeResponse,
        success: finalBillingData.paymeResponse?.success,
        transaction: finalBillingData.paymeResponse?.transaction,
      });

      // Validate Payme response
      try {
        if (!validatePaymentResponse(finalBillingData.paymeResponse)) {
          setError("Error procesando el pago. Por favor intente nuevamente.");
          setIsProcessingPayment(false);
          return;
        }
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError("Error procesando el pago. Por favor intente nuevamente.");
        }
        setIsProcessingPayment(false);
        return;
      }
    }

    // Validate all sections with our enhanced validation system
    const shippingValidation = validateShippingData(shippingData);
    const customerValidation = validateCustomerData(customerData);
    const billingValidation = validateBillingData(finalBillingData);

    // Check for validation errors using the hasErrors property
    if (
      shippingValidation.hasErrors ||
      customerValidation.hasErrors ||
      billingValidation.hasErrors
    ) {
      console.error("Checkout: Validation failed", {
        shippingError: shippingValidation,
        customerError: customerValidation,
        billingError: billingValidation,
      });
      return;
    }

    try {
      setIsProcessingPayment(true); // Show overlay

      const payload = prepareOrderPayload({
        customerData,
        shippingData,
        billingData: finalBillingData,
        cartGroups,
      });
      console.log("Checkout: Order payload", payload);
      const cartId = localStorage.getItem("cartId");

      const response = await finalizeCartCheckout(
        cartId || "",
        cartGroups.map((group) => group.id),
        payload
      );

      console.log("Checkout: finalizeCartCheckout response", response);
      if (response.error) {
        console.error("Checkout: API returned an error", response.error);
        setError(response.error);
        setIsProcessingPayment(false); // Hide overlay on error
        return;
      }
      if (!response.orderSuccessData) {
        console.error("Checkout: Missing orderSuccessData", response);
        setError("Order submission failed: no order success data received.");
        setIsProcessingPayment(false); // Hide overlay on error
        return;
      }

      // Track purchase event
      trackEcommerceEvent("purchase", {
        transaction_id:
          response.orderSuccessData?.orderId || `order-${Date.now()}`,
        value: cleanTotals.grandTotal, // Use clean pricing grand total
        currency: "PEN",
        tax: 0,
        shipping: cleanTotals.deliveryFeeTotal, // Use clean pricing delivery total
        items: cartGroups.flatMap((group: any) =>
          group.items.map((item: any) => ({
            item_id: item.product?.id,
            item_name: item.product?.name || "Unknown Product",
            price: item.price?.discount?.newPrice || item.price || 0,
            quantity: item.quantity,
          }))
        ),
        payment_type: finalBillingData.paymentMethod || "unknown",
      });

      // Enhanced Meta Pixel tracking for purchase with comprehensive catalog data
      metaPixelCatalogService.trackPurchase(
        response.orderSuccessData?.orderId || `order-${Date.now()}`,
        cartGroups.flatMap((group: any) =>
          group.items.map((item: any) => ({
            id: item.product?.id,
            name: item.product?.name || "Unknown Product",
            price: item.price?.discount?.newPrice || item.price || 0,
            quantity: item.quantity,
            category: item.product?.category?.name || "Unknown",
            brand: item.product?.partner?.name || "Unknown",
            sku: item.product?.sku || "",
            customizableOptions: item.customizableOptions || [],
            groupingAttributes: item.groupingAttributes || [],
          }))
        ),
        cartTotals.cartTotal,
        finalBillingData.paymentMethod || "unknown"
      );

      // Persist order data for OrderComplete page reloads
      if (response.orderSuccessData) {
        sessionStorage.setItem(
          "orderSuccessData",
          JSON.stringify(response.orderSuccessData)
        );

        // FIXED: Clear cart data immediately when order is successful
        localStorage.removeItem("cartId");

        // Clear cartId cookie to prevent conflicts with new carts
        const isDevelopment = process.env.NODE_ENV !== "production";
        const domain = isDevelopment
          ? undefined
          : `.${process.env.REACT_APP_BASE_DOMAIN || "evefant.com"}`;

        document.cookie = `cartId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; ${
          domain ? `domain=${domain};` : ""
        } SameSite=lax`;

        // Also try clearing without domain for local development
        if (isDevelopment) {
          document.cookie = `cartId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=lax`;
        }

        console.log("✅ Cart cleared after successful order submission");
      }
      console.log(
        "Checkout: Navigating to /order-success with",
        response.orderSuccessData
      );
      navigate("/order-success", { state: response.orderSuccessData });
    } catch (error) {
      console.error("Checkout: Order submission error:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Error procesando tu orden. Intenta nuevamente."
      );
      setIsProcessingPayment(false); // Hide overlay on error
    }
  };

  // Add a handler for payment errors
  const handlePaymentError = (error: Error) => {
    console.error("Payment error:", error);
    setError(
      error.message || "Error procesando el pago. Por favor intente nuevamente."
    );
    setIsProcessingPayment(false); // Important: hide the overlay on error
    setLoadingMessage("Procesando pago"); // Reset loading message
  };

  // Add method to handle payment response validation
  const validatePaymentResponse = (response: any) => {
    // Check if response has valid structure and success status code "00"
    if (
      response &&
      response.transaction &&
      response.transaction.meta &&
      response.transaction.meta.status &&
      response.transaction.meta.status.code === "00"
    ) {
      return true;
    }

    // If not successful, get the error message
    let errorMessage = "Error procesando el pago.";

    if (
      response?.transaction?.meta?.status?.message_ilgn &&
      response.transaction.meta.status.message_ilgn.length > 0
    ) {
      errorMessage = response.transaction.meta.status.message_ilgn[0].value;
    }

    // Throw error with the message
    throw new Error(errorMessage);
  };

  const handleShippingConfirm = () => {
    setShippingConfirmed(true);
    // Scroll to customer data section after a short delay to allow rendering
    setTimeout(() => {
      if (customerDataRef.current) {
        scrollToNextSection(customerDataRef.current);
      }
    }, 100);

    // Track shipping information added
    trackEcommerceEvent("add_shipping_info", {
      currency: "PEN",
      value: cartTotals.cartTotal,
      items: cartGroups.flatMap((group: any) =>
        group.items.map((item: any) => ({
          item_id: item.product?.id,
          item_name: item.product?.name || "Unknown Product",
          price: item.price?.discount?.newPrice || item.price || 0,
          quantity: item.quantity,
        }))
      ),
      shipping_tier: shippingData.shippingMethod || "standard",
    });
  };

  const handleCustomerDataConfirm = () => {
    setCustomerDataConfirmed(true);
    // Scroll to billing section after a short delay
    setTimeout(() => {
      if (billingElementRef.current) {
        scrollToNextSection(billingElementRef.current);
      }
    }, 100);

    // Track customer information added
    trackEvent("add_customer_info", {
      has_email: !!customerData.email,
      has_phone: !!customerData.phone,
    });
  };

  const handlePaymentProcessing = () => {
    setLoadingMessage("Validando datos de tarjeta...");
    setIsProcessingPayment(true);

    // After a delay, update the message to show order processing
    setTimeout(() => {
      if (isProcessingPayment) {
        setLoadingMessage("Generando orden...");
      }
    }, 3000); // Change message after 3 seconds
  };

  const allConfirmed =
    shippingConfirmed && customerDataConfirmed && billingConfirmed;

  return (
    <div className="container">
      <div className={styles.checkoutContainer}>
        {isProcessingPayment && (
          <div className={styles.overlay}>
            <div className={styles.spinner}></div>
            <div className={styles.loadingMessage}>{loadingMessage}</div>
          </div>
        )}
        <h1 className={styles.hidden}>Checkout</h1>
        <div className={styles.backButtonWrapper}>
          <div
            className={styles.backButtonContainer}
            onClick={() => navigate("/cart")}
            role="button"
          >
            <ArrowBackIcon sx={{ fontSize: 18, marginRight: 1 }} />
            <span className={styles.backButton}>Volver</span>
          </div>
        </div>

        {/* Reservation Timer */}
        {reservationTime.expiryTime && (
          <div
            className={`${styles.reservationTimer} ${
              reservationTime.isExpiring ? styles.expiring : ""
            }`}
          >
            <div className={styles.timerIcon}>⏱️</div>
            <div className={styles.timerContent}>
              <p className={styles.timerTitle}>
                Tiempo restante para completar su compra:
              </p>
              <p className={styles.timerValue}>
                {Math.floor(reservationTime.timeLeft / 60)}:
                {(reservationTime.timeLeft % 60).toString().padStart(2, "0")}
              </p>
            </div>
            {reservationTime.isExpiring && (
              <p className={styles.timerWarning}>
                ¡Su reserva está por expirar! Complete su compra pronto.
              </p>
            )}
          </div>
        )}
        <div className={styles.checkoutContent}>
          <div className={styles.leftColumn}>
            <div ref={shippingRef}>
              <Shipping
                onConfirm={handleShippingConfirm}
                onChange={setShippingData}
                onEdit={() => setShippingConfirmed(false)}
              />
            </div>
            {shippingConfirmed && (
              <div ref={customerDataRef}>
                <CustomerData
                  onConfirm={handleCustomerDataConfirm}
                  onChange={setCustomerData}
                  onEdit={() => setCustomerDataConfirmed(false)}
                />
              </div>
            )}
            {customerDataConfirmed && (
              <div ref={billingElementRef}>
                <Billing
                  ref={billingRef}
                  onConfirm={() => setBillingConfirmed(true)}
                  onChange={setBillingData}
                  onEdit={() => setBillingConfirmed(false)}
                  onOrderSubmit={handleOrderSubmit}
                  onPaymentProcessing={handlePaymentProcessing}
                  onPaymentError={handlePaymentError} // Add this new prop
                  amount={cleanTotals.grandTotal}
                  customerData={customerData}
                  shippingData={shippingData}
                />
              </div>
            )}
          </div>
          <div className={styles.rightColumn}>
            {loading ? (
              <div>Loading...</div>
            ) : (
              <CheckoutSummary
                billingConfirmed={allConfirmed}
                groups={cartGroups}
                totals={cleanTotals}
                onSubmit={async () => {}}
                appliedCouponCode={appliedCouponCode}
              />
            )}
            {error && <div className={styles.error}>{error}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
